// apps/agent-studio/src/shared/analytics/client.ts
import * as amplitude from '@amplitude/unified';
import { logger } from '@/lib/utils/logger';
import { buildIdentify, type IdentifyInput } from './identify';
import { currentRoute } from './standard-props';
import type { EventName, EventProps } from './events';

let isInitialized = false;

export function initAnalytics(): void {
  const apiKey = import.meta.env.AMPLITUDE_API_KEY;
  if (!apiKey) {
    logger.warn('[analytics] AMPLITUDE_API_KEY not set, analytics disabled');
    return;
  }

  try {
    void amplitude.initAll(apiKey, {
      analytics: { autocapture: true, appVersion: import.meta.env.SENTRY_RELEASE },
      sessionReplay: { sampleRate: 1 },
    });
    isInitialized = true;
  } catch (err) {
    logger.error('[analytics] init failed', err);
  }
}

function safe<Args extends unknown[]>(
  name: string,
  fn: (...args: Args) => void,
): (...args: Args) => void {
  return (...args) => {
    if (!isInitialized) return;
    try {
      fn(...args);
    } catch (err) {
      // First arg is the event name for track(), the group type for setGroup(),
      // or omitted for reset(); including it gives on-call enough context.
      logger.error(`[analytics] ${name} failed`, { firstArg: args[0], err });
    }
  };
}

function _track<E extends EventName>(name: E, props: EventProps<E>): void {
  amplitude.track(name, {
    ...props,
    route: currentRoute(),
  });
}

function _identify(input: IdentifyInput): void {
  amplitude.setUserId(input.userId);
  amplitude.identify(buildIdentify(input));
}

function _setGroup(groupType: string, groupValue: string): void {
  amplitude.setGroup(groupType, groupValue);
}

function _reset(): void {
  amplitude.reset();
}

// `safe()` is generic over an unconstrained Args tuple, so its inferred return
// type collapses `_track`'s generic. The cast restores the per-call generic
// signature so call sites get compile-time prop checking. Documented here so
// a future drift in `_track`'s signature is reviewed against this cast.
/* eslint-disable i18next/no-literal-string -- SDK method names used in the failure log */
export const track = safe('track', _track) as <E extends EventName>(
  name: E,
  props: EventProps<E>,
) => void;
export const identify = safe('identify', _identify);
export const setGroup = safe('setGroup', _setGroup);
export const reset = safe('reset', _reset);
/* eslint-enable i18next/no-literal-string */
